import apiClient from './api.service';
import type { ApiResponse } from '@/types';

export interface Document {
  id: string;
  company_id: string;
  document_number: string;
  folder_id?: string;
  document_name: string;
  document_title?: string;
  description?: string;
  file_name: string;
  file_path: string;
  file_url?: string;
  file_size_bytes: number;
  file_type?: string;
  mime_type?: string;
  version_number: string;
  is_latest_version: boolean;
  entity_type?: string;
  entity_id?: string;
  document_type?: string;
  document_category?: string;
  tags?: string[];
  keywords?: string;
  document_date?: string;
  reference_number?: string;
  is_confidential: boolean;
  access_level: string;
  expiry_date?: string;
  document_status: string;
  download_count: number;
  view_count: number;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentsPagedResult {
  items: Document[];
  total: number;
  page: number;
  limit: number;
}

class DocumentService {
  async uploadDocument(
    file: File,
    data: {
      document_name?: string;
      entity_type?: string;
      entity_id?: string;
      document_type?: string;
      document_category?: string;
      folder_id?: string;
      description?: string;
      tags?: string[];
      keywords?: string;
      is_confidential?: boolean;
      access_level?: string;
      expiry_date?: string;
    }
  ): Promise<Document> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (data.document_name) formData.append('document_name', data.document_name);
    if (data.entity_type) formData.append('entity_type', data.entity_type);
    if (data.entity_id) formData.append('entity_id', data.entity_id);
    if (data.document_type) formData.append('document_type', data.document_type);
    if (data.document_category) formData.append('document_category', data.document_category);
    if (data.folder_id) formData.append('folder_id', data.folder_id);
    if (data.description) formData.append('description', data.description);
    if (data.tags) formData.append('tags', JSON.stringify(data.tags));
    if (data.keywords) formData.append('keywords', data.keywords);
    if (data.is_confidential !== undefined) formData.append('is_confidential', String(data.is_confidential));
    if (data.access_level) formData.append('access_level', data.access_level);
    if (data.expiry_date) formData.append('expiry_date', data.expiry_date);

    // Do not set Content-Type manually — axios must attach multipart boundary.
    const response = await apiClient.post('/documents/upload', formData, {
      timeout: 120000, // 2 minutes for file uploads
    });
    const payload = response as unknown as ApiResponse<Document>;
    if (payload?.data) return payload.data;
    return response as unknown as Document;
  }

  async getDocuments(filters?: {
    entity_type?: string;
    entity_id?: string;
    folder_id?: string;
    document_type?: string;
    document_status?: string;
    search?: string;
  }): Promise<Document[]> {
    const response = await apiClient.get<Document[]>('/documents', { params: filters });
    const raw = response.data as unknown;
    if (Array.isArray(raw)) return raw;
    return [];
  }

  async getDocumentsPaged(params: {
    page: number;
    limit: number;
    search?: string;
    entity_type?: string;
    document_type?: string;
    document_status?: string;
  }): Promise<DocumentsPagedResult> {
    const { page, limit, search, entity_type, document_type, document_status } = params;
    const response = await apiClient.get<DocumentsPagedResult>('/documents', {
      params: {
        paged: 1,
        page,
        limit,
        search: search?.trim() || undefined,
        entity_type: entity_type?.trim() || undefined,
        document_type: document_type?.trim() || undefined,
        document_status: document_status?.trim() || undefined,
      },
    });
    const payload = response.data as unknown;
    if (
      payload &&
      typeof payload === 'object' &&
      'items' in payload &&
      Array.isArray((payload as DocumentsPagedResult).items)
    ) {
      return payload as DocumentsPagedResult;
    }
    return { items: [], total: 0, page: params.page, limit: params.limit };
  }

  async getDocumentsByEntity(entityType: string, entityId: string): Promise<Document[]> {
    try {
      const response = await apiClient.get<Document[]>(`/documents/entity/${entityType}/${entityId}`);
      const raw = response.data as unknown;
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  }

  async getDocumentById(id: string): Promise<Document> {
    const response = await apiClient.get(`/documents/${id}`);
    // Handle different response structures
    if (Array.isArray(response.data)) {
      return response.data[0];
    }
    return response.data?.data || response.data;
  }

  async updateDocument(id: string, data: Partial<Document>): Promise<Document> {
    const response = await apiClient.put(`/documents/${id}`, data);
    return response.data.data;
  }

  async deleteDocument(id: string): Promise<void> {
    await apiClient.delete(`/documents/${id}`);
  }

  async linkDocument(id: string, entityType: string, entityId: string): Promise<Document> {
    const response = await apiClient.post(`/documents/${id}/link`, {
      entity_type: entityType,
      entity_id: entityId,
    });
    return response.data.data;
  }

  async downloadDocument(id: string): Promise<Blob> {
    // Use the axios client directly to get proper blob response
    const client = apiClient.getClient();
    const response = await client.get(`/documents/${id}/download`, {
      responseType: 'blob',
    });

    // Ensure we return a Blob
    if (response.data instanceof Blob) {
      return response.data;
    }
    
    // If it's not a Blob, try to create one
    if (typeof response.data === 'string') {
      return new Blob([response.data]);
    }
    
    // Last resort: convert to Blob
    return new Blob([response.data], { type: 'application/octet-stream' });
  }

  /** Open document file in a new browser tab (blob URL). Falls back to download stream if /view fails. */
  async openDocumentInNewTab(id: string): Promise<void> {
    const readBlobErrorMessage = async (err: unknown): Promise<string | null> => {
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      if (!data) return null;
      try {
        const text =
          typeof Blob !== 'undefined' && data instanceof Blob
            ? await data.text()
            : typeof data === 'string'
              ? data
              : JSON.stringify(data);
        const parsed = JSON.parse(text) as { message?: string };
        return parsed?.message || null;
      } catch {
        return null;
      }
    };

    let blob: Blob;
    try {
      blob = await this.viewDocument(id);
    } catch (viewErr) {
      try {
        blob = await this.downloadDocument(id);
      } catch (downloadErr) {
        const msg =
          (await readBlobErrorMessage(downloadErr)) ||
          (await readBlobErrorMessage(viewErr)) ||
          (downloadErr as { message?: string })?.message ||
          (viewErr as { message?: string })?.message ||
          'Could not open document';
        throw new Error(msg);
      }
    }
    if (blob.type?.includes('application/json')) {
      const text = await blob.text();
      let msg = 'Could not open document';
      try {
        const parsed = JSON.parse(text) as { message?: string };
        if (parsed?.message) msg = parsed.message;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    const typed =
      blob.type && blob.type !== 'application/octet-stream'
        ? blob
        : new Blob([blob], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(typed);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  }

  async viewDocument(id: string): Promise<Blob> {
    // Use the axios client directly to get proper blob response
    const client = apiClient.getClient();
    const response = await client.get(`/documents/${id}/view`, {
      responseType: 'blob',
    });

    // Ensure we return a Blob
    if (response.data instanceof Blob) {
      return response.data;
    }
    
    // If it's not a Blob, try to create one
    if (typeof response.data === 'string') {
      return new Blob([response.data]);
    }
    
    // Last resort: convert to Blob
    return new Blob([response.data], { type: 'application/octet-stream' });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

export const documentService = new DocumentService();

